import React, { useRef, useCallback } from 'react';
import { toPng } from 'html-to-image';

// ===========================
//  MOCK ACME ASSET TEMPLATES
// ===========================

// --- 1. SLIDE DECK TEMPLATE ---
const DeckTemplate = ({ title, content }) => (
  <div className="asset-template deck-template" style={{
    background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
    padding: '3rem', borderRadius: '12px', border: '1px solid #c7d2fe',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', color: '#1e293b',
    fontFamily: 'sans-serif', minHeight: '400px', display: 'flex', flexDirection: 'column'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{ width: '24px', height: '24px', background: '#7C3AED', borderRadius: '4px' }}></div>
        <span style={{ fontWeight: '700', letterSpacing: '0.05em', color: '#7C3AED' }}>ACME, INC.</span>
      </div>
      <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '500' }}>CONFIDENTIAL</span>
    </div>

    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <h1 style={{ fontSize: '2.5rem', fontWeight: '800', margin: '0 0 1rem 0', color: '#1e293b', lineHeight: '1.1' }}>
        {title}
      </h1>
      <h2 style={{ fontSize: '1.5rem', fontWeight: '500', margin: '0 0 2rem 0', color: '#7C3AED' }}>
        {content?.subtitle || "Strategic Overview & Key Insights"}
      </h2>
      <div style={{ width: '60px', height: '4px', background: '#7C3AED', borderRadius: '2px' }}></div>
    </div>
  </div>
);

// --- 2. WHITEBOARD TEMPLATE ---
const WhiteboardTemplate = ({ title, content }) => (
  <div className="asset-template whiteboard-template" style={{
    background: '#fff', padding: '2rem', borderRadius: '4px',
    border: '3px solid #e2e8f0', boxShadow: 'inset 0 0 20px rgba(0,0,0,0.05)',
    fontFamily: '"Comic Sans MS", "Marker Felt", sans-serif', color: '#334155',
    minHeight: '400px', position: 'relative'
  }}>
    <div style={{ position: 'absolute', top: '10px', left: '10px', width: '15px', height: '15px', borderRadius: '50%', border: '2px solid #cbd5e1' }}></div>
    <div style={{ position: 'absolute', top: '10px', right: '10px', width: '15px', height: '15px', borderRadius: '50%', border: '2px solid #cbd5e1' }}></div>
    
    <h2 style={{ fontSize: '1.75rem', borderBottom: '2px dashed #94a3b8', paddingBottom: '0.5rem', marginBottom: '1.5rem' }}>
      <span style={{ color: '#7C3AED' }}>#</span> {title}
    </h2>
    <div style={{ fontSize: '1.1rem', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
      {content?.sketches || "Loading sketches..."}
    </div>
  </div>
);

// --- 3. PLAYBOOK / DOCUMENT TEMPLATE ---
const DocumentTemplate = ({ title, content }) => (
  <div className="asset-template document-template" style={{
    background: '#fff', padding: '3rem', borderRadius: '2px',
    border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    fontFamily: 'Georgia, serif', color: '#374151', maxWidth: '650px', margin: '0 auto'
  }}>
    <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
      <div style={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>
        ACME Global Enablement
      </div>
      <h1 style={{ fontSize: '2rem', margin: '0', color: '#111827' }}>{title}</h1>
    </div>
    
    <div style={{ lineHeight: '1.7' }}>
      {content?.sections ? content.sections.map((section, idx) => (
        <div key={idx} style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem', marginBottom: '1rem', color: '#1f2937', fontFamily: 'sans-serif' }}>
            {section.heading}
          </h3>
          <p>{section.body}</p>
        </div>
      )) : <p>Content goes here...</p>}
    </div>
  </div>
);

// ===========================
//   MAIN MODAL COMPONENT
// ===========================

const AssetModal = ({ asset, onClose }) => {
  const contentRef = useRef(null);

  const getTemplate = () => {
    if (!asset) return null;
    switch (asset.templateType) {
      case 'Deck': return <DeckTemplate title={asset.title} content={asset.sampleContent} />;
      case 'Whiteboard': return <WhiteboardTemplate title={asset.title} content={asset.sampleContent} />;
      case 'Document': return <DocumentTemplate title={asset.title} content={asset.sampleContent} />;
      default: return <div style={{padding: '2rem'}}>Template not found for type: {asset.type}</div>;
    }
  };

  const handleExport = useCallback(() => {
    if (contentRef.current === null) return;

    toPng(contentRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: null })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.download = `ACME_Asset_${asset.title.replace(/\s+/g, '_')}.png`;
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.error('Export failed:', err);
      });
  }, [asset]);

  if (!asset) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      padding: '2rem'
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: '16px', overflow: 'hidden',
        width: '100%', maxWidth: '900px', maxHeight: '90vh',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', display: 'flex', flexDirection: 'column'
      }} onClick={e => e.stopPropagation()}>
        
        {/* Modal Header */}
        <div style={{
          padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc'
        }}>
          <div style={{display: 'flex', flexDirection: 'column'}}>
            <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {asset.type} Template
            </span>
            <h3 style={{ margin: '0.25rem 0 0', fontSize: '1.125rem', color: '#0f172a' }}>{asset.title}</h3>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={handleExport} style={{
              padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #7C3AED',
              background: '#fff', color: '#7C3AED', fontWeight: '600', fontSize: '0.875rem',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'all 0.2s'
            }}>
              Export PNG
            </button>
            <button onClick={onClose} style={{
              padding: '0.5rem', borderRadius: '6px', border: 'none', background: '#f1f5f9',
              color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              X
            </button>
          </div>
        </div>

        {/* Modal Body (Scrollable) */}
        <div style={{ padding: '2rem', overflowY: 'auto', background: '#f1f5f9', flex: 1 }}>
          <div ref={contentRef} style={{ boxSizing: 'border-box' }}>
            {getTemplate()}
          </div>
        </div>

      </div>
    </div>
  );
};

export default AssetModal;
